import { useEffect, useMemo } from "react";
import { createOwnerMark, profileNameRowOf, type LocalBellRow } from "@heraion/ui";

import { useShellProfile } from "../ThemeProfileBridge";
import { webStorage } from "../webStorage";

// #178, the web's side: the first profile write failed at the invitation
// gate, so the athlete is in with no name. One row of the bell until a
// profile read carries a name; the tap opens Settings > Account with the name
// field focused. The row is the phone's (profileNameRowOf). The mark is per
// account on this browser: the Cognito `sub` is unique per pool, so dev and
// prod never share it (the way useAccountKey names the web's per-account keys).

const mark = createOwnerMark<string>({
  base: "jl.bell.profileName.v1",
  parse: (raw) => raw,
  serialize: (v) => v,
  label: "missing name",
  storage: webStorage,
  keyOf: (owner) => `jl.bell.profileName.v1.${owner}`,
});

/** The sign-in calls this when the profile row was not written. `at` is the
 *  row's date and its version. */
export const recordProfileNameMissing = mark.record;

/** The row, or null. It goes once a profile read carries a name (the
 *  athlete saved it in Account), and the mark with it. */
export function useProfileNameBell(owner: string): LocalBellRow | null {
  const { doc } = useShellProfile();
  const at = mark.useMark(owner);
  // Until the profile is read the row stays out (mobile's `loaded`): an old
  // mark of an account that has a name must not flash.
  const read = doc !== null;
  const named = read && String(doc["name"] ?? "").trim() !== "";
  useEffect(() => {
    if (named && at) void mark.forget(owner);
  }, [owner, named, at]);
  return useMemo(
    () => (at && read && !named ? profileNameRowOf(at) : null),
    [at, read, named],
  );
}
